/**
 * Product Gallery — standalone JS (zero OLD dependencies)
 *
 * Features:
 * - Thumbnail click → main image navigation
 * - Mobile swipe carousel with scroll-snap
 * - Variant image filtering (listens for variant:change)
 * - Zoom on click (optional, CSS transform)
 * - Video play/pause
 * - Keyboard navigation (arrow keys)
 */
(function () {
  'use strict';

  var gallery = document.querySelector('[data-product-gallery]');
  if (!gallery) return;

  var mainWrapper = gallery.querySelector('[data-gallery-main]');
  var thumbnailContainer = gallery.querySelector('[data-gallery-thumbnails]');
  var dotsContainer = gallery.querySelector('[data-gallery-dots]');
  var enableZoom = gallery.hasAttribute('data-enable-zoom');
  var videoAutoplay = gallery.hasAttribute('data-video-autoplay');

  // ========================================================================
  // HELPERS
  // ========================================================================

  function getVisibleSlides() {
    return Array.from(mainWrapper.querySelectorAll('.product-gallery-v2__slide:not([hidden])'));
  }

  function getActiveSlide() {
    return mainWrapper.querySelector('.product-gallery-v2__slide[data-active]');
  }

  function setActiveMedia(mediaId) {
    // Slides
    var slides = mainWrapper.querySelectorAll('.product-gallery-v2__slide');
    slides.forEach(function (slide) {
      if (slide.dataset.mediaId === String(mediaId)) {
        slide.setAttribute('data-active', '');
        // Pause any other video, play this if video
        handleVideoState(slide, true);
      } else {
        slide.removeAttribute('data-active');
        handleVideoState(slide, false);
      }
    });

    // Thumbnails
    if (thumbnailContainer) {
      var thumbs = thumbnailContainer.querySelectorAll('.product-gallery-v2__thumbnail');
      thumbs.forEach(function (thumb) {
        thumb.setAttribute('aria-current', thumb.dataset.mediaId === String(mediaId) ? 'true' : 'false');
      });
      // Scroll active thumbnail into view (confined to container, no page scroll)
      var activeThumb = thumbnailContainer.querySelector('[aria-current="true"]');
      if (activeThumb) {
        activeThumb.parentElement.scrollTo({
          left: activeThumb.offsetLeft - (activeThumb.parentElement.offsetWidth - activeThumb.offsetWidth) / 2,
          top: activeThumb.offsetTop - (activeThumb.parentElement.offsetHeight - activeThumb.offsetHeight) / 2,
          behavior: 'smooth'
        });
      }
    }

    // Dots
    if (dotsContainer) {
      var dots = dotsContainer.querySelectorAll('.product-gallery-v2__dot');
      dots.forEach(function (dot) {
        dot.setAttribute('aria-current', dot.dataset.mediaId === String(mediaId) ? 'true' : 'false');
      });
    }

    // Mobile: scroll to the right slide (confined to container, no page scroll)
    if (window.innerWidth < 1000) {
      var targetSlide = mainWrapper.querySelector('.product-gallery-v2__slide[data-media-id="' + mediaId + '"]');
      if (targetSlide && targetSlide.parentElement) {
        var container = targetSlide.parentElement;
        container.scrollTo({
          left: targetSlide.offsetLeft - (container.offsetWidth - targetSlide.offsetWidth) / 2,
          behavior: 'smooth'
        });
      }
    }
  }

  // ========================================================================
  // VIDEO HANDLING
  // ========================================================================

  function handleVideoState(slide, shouldPlay) {
    var video = slide.querySelector('video');
    if (!video) return;
    if (shouldPlay && videoAutoplay) {
      video.play();
    } else if (!shouldPlay) {
      video.pause();
    }
  }

  // ========================================================================
  // THUMBNAIL CLICK
  // ========================================================================

  if (thumbnailContainer) {
    thumbnailContainer.addEventListener('click', function (e) {
      var thumb = e.target.closest('.product-gallery-v2__thumbnail');
      if (!thumb) return;
      setActiveMedia(thumb.dataset.mediaId);
    });
  }

  // ========================================================================
  // DOTS CLICK
  // ========================================================================

  if (dotsContainer) {
    dotsContainer.addEventListener('click', function (e) {
      var dot = e.target.closest('.product-gallery-v2__dot');
      if (!dot) return;
      setActiveMedia(dot.dataset.mediaId);
    });
  }

  // ========================================================================
  // MOBILE SWIPE — scroll-snap observer
  // ========================================================================

  if (mainWrapper) {
    var scrollTimeout;
    mainWrapper.addEventListener('scroll', function () {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(function () {
        var slides = getVisibleSlides();
        var wrapperRect = mainWrapper.getBoundingClientRect();
        var centerX = wrapperRect.left + wrapperRect.width / 2;

        var closest = null;
        var closestDist = Infinity;
        slides.forEach(function (slide) {
          var rect = slide.getBoundingClientRect();
          var dist = Math.abs(rect.left + rect.width / 2 - centerX);
          if (dist < closestDist) {
            closestDist = dist;
            closest = slide;
          }
        });

        if (closest && closest.dataset.mediaId) {
          // Update active state without scrolling (we're already there)
          var slides2 = mainWrapper.querySelectorAll('.product-gallery-v2__slide');
          slides2.forEach(function (s) {
            if (s === closest) {
              s.setAttribute('data-active', '');
            } else {
              s.removeAttribute('data-active');
            }
          });

          // Update thumbnails and dots
          var mediaId = closest.dataset.mediaId;
          if (thumbnailContainer) {
            thumbnailContainer.querySelectorAll('.product-gallery-v2__thumbnail').forEach(function (t) {
              t.setAttribute('aria-current', t.dataset.mediaId === mediaId ? 'true' : 'false');
            });
          }
          if (dotsContainer) {
            dotsContainer.querySelectorAll('.product-gallery-v2__dot').forEach(function (d) {
              d.setAttribute('aria-current', d.dataset.mediaId === mediaId ? 'true' : 'false');
            });
          }
        }
      }, 100);
    }, { passive: true });
  }

  // ========================================================================
  // KEYBOARD NAVIGATION
  // ========================================================================

  gallery.setAttribute('tabindex', '0');
  gallery.addEventListener('keydown', function (e) {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();

    var slides = getVisibleSlides();
    var active = getActiveSlide();
    if (!active || slides.length < 2) return;

    var currentIndex = slides.indexOf(active);
    var nextIndex;

    if (e.key === 'ArrowLeft') {
      nextIndex = currentIndex > 0 ? currentIndex - 1 : slides.length - 1;
    } else {
      nextIndex = currentIndex < slides.length - 1 ? currentIndex + 1 : 0;
    }

    setActiveMedia(slides[nextIndex].dataset.mediaId);
  });

  // ========================================================================
  // ZOOM ON CLICK (desktop only)
  // ========================================================================

  if (enableZoom) {
    mainWrapper.addEventListener('click', function (e) {
      if (window.innerWidth < 1000) return;
      var slide = e.target.closest('.product-gallery-v2__slide');
      if (!slide || slide.dataset.mediaType !== 'image') return;

      var isZoomed = slide.hasAttribute('data-zoomed');
      if (isZoomed) {
        slide.removeAttribute('data-zoomed');
        slide.style.removeProperty('--zoom-x');
        slide.style.removeProperty('--zoom-y');
      } else {
        var rect = slide.getBoundingClientRect();
        var x = ((e.clientX - rect.left) / rect.width * 100).toFixed(1) + '%';
        var y = ((e.clientY - rect.top) / rect.height * 100).toFixed(1) + '%';
        slide.style.setProperty('--zoom-x', x);
        slide.style.setProperty('--zoom-y', y);
        slide.style.setProperty('--zoom-level', '2');
        slide.setAttribute('data-zoomed', '');
      }
    });

    // Track mouse for zoom position update
    mainWrapper.addEventListener('mousemove', function (e) {
      if (window.innerWidth < 1000) return;
      var slide = e.target.closest('.product-gallery-v2__slide[data-zoomed]');
      if (!slide) return;
      var rect = slide.getBoundingClientRect();
      var x = ((e.clientX - rect.left) / rect.width * 100).toFixed(1) + '%';
      var y = ((e.clientY - rect.top) / rect.height * 100).toFixed(1) + '%';
      slide.style.setProperty('--zoom-x', x);
      slide.style.setProperty('--zoom-y', y);
    });
  }

  // ========================================================================
  // VARIANT IMAGE FILTERING
  // Listens for variant:change dispatched by the variant picker JS
  // ========================================================================

  document.addEventListener('variant:change', function (e) {
    var detail = e.detail;
    if (!detail) return;

    var selectedVariant = detail.variant;
    if (!selectedVariant) return;

    var prod = detail.product || null;

    // --- 1. Hide all variant-media slides, then show selected variant's ---
    var variantSlides = mainWrapper.querySelectorAll('.product-gallery-v2__slide[data-variant-media]');
    variantSlides.forEach(function (s) {
      s.setAttribute('hidden', '');
      s.removeAttribute('data-active');
    });

    if (selectedVariant.featured_media) {
      var featuredSlide = mainWrapper.querySelector(
        '.product-gallery-v2__slide[data-media-id="' + selectedVariant.featured_media.id + '"]'
      );
      if (featuredSlide) {
        featuredSlide.removeAttribute('hidden');
      }
    }

    // --- 2. Alt-tag filtering on non-variant media (existing logic) ---
    var newFiltered = [];
    if (prod && prod.variants && prod.variants.length > 1) {
      prod.media.forEach(function (media) {
        if (media.alt && media.alt.indexOf('#') !== -1 && media.alt !== prod.title) {
          var altParts = media.alt.split('#');
          var groupParts = altParts[altParts.length - 1].split('_');
          var optionName = groupParts[0];
          var groupValue = (groupParts[groupParts.length - 1] || '').trim().toLowerCase();

          var optionIndex = -1;
          for (var i = 0; i < prod.options.length; i++) {
            if (prod.options[i].toLowerCase() === optionName.toLowerCase()) {
              optionIndex = i;
              break;
            }
          }

          if (optionIndex >= 0) {
            var selectedValue = (selectedVariant.options[optionIndex] || '').toLowerCase();
            if (selectedValue !== groupValue) {
              newFiltered.push(media.position);
            }
          }
        }
      });
    }

    // Apply alt-tag filtering to non-variant slides only
    var nonVariantSlides = mainWrapper.querySelectorAll('.product-gallery-v2__slide:not([data-variant-media])');
    nonVariantSlides.forEach(function (slide) {
      var pos = parseInt(slide.dataset.mediaPosition, 10);
      if (newFiltered.indexOf(pos) !== -1) {
        slide.setAttribute('hidden', '');
        slide.removeAttribute('data-active');
      } else {
        slide.removeAttribute('hidden');
      }
    });

    // Apply alt-tag filtering to thumbnails (variant media not in thumbnails)
    if (thumbnailContainer) {
      thumbnailContainer.querySelectorAll('.product-gallery-v2__thumbnail').forEach(function (thumb) {
        var pos = parseInt(thumb.dataset.mediaPosition, 10);
        if (newFiltered.indexOf(pos) !== -1) {
          thumb.setAttribute('hidden', '');
        } else {
          thumb.removeAttribute('hidden');
        }
      });
    }

    // Apply alt-tag filtering to dots (variant media not in dots)
    if (dotsContainer) {
      dotsContainer.querySelectorAll('.product-gallery-v2__dot').forEach(function (dot) {
        var mediaId = dot.dataset.mediaId;
        var slide = mainWrapper.querySelector('.product-gallery-v2__slide:not([data-variant-media])[data-media-id="' + mediaId + '"]');
        if (slide && slide.hasAttribute('hidden')) {
          dot.setAttribute('hidden', '');
        } else {
          dot.removeAttribute('hidden');
        }
      });
    }

    // --- 3. Navigate to variant's featured media or first visible ---
    if (selectedVariant.featured_media) {
      setActiveMedia(selectedVariant.featured_media.id);
    } else {
      var firstVisible = mainWrapper.querySelector('.product-gallery-v2__slide:not([hidden])');
      if (firstVisible) {
        setActiveMedia(firstVisible.dataset.mediaId);
      }
    }
  });
})();
